import React, { useEffect, useState } from "react";
import { Loader2, Search, Trash2, Archive, ChevronDown, ChevronUp, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../console/api";

const AUBERGINE = "#5A4565";
const CREAM = "#FBF6EA";
const EDGE = "#EFE4CC";
const BERRY = "#6E1E3A";
const GREEN = "#2F4A2A";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function AdminCompletedHistorical() {
  const [rows, setRows] = useState([]);
  const [years, setYears] = useState([]);
  const [summary, setSummary] = useState(null);
  const [legacy, setLegacy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [q, setQ] = useState("");
  const [year, setYear] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = async (query = q, yr = year) => {
    setLoading(true);
    try {
      const [l, s, ls] = await Promise.all([
        api.get("/admin/historical-orders", { params: { q: query, year: yr } }),
        api.get("/admin/historical-orders/summary"),
        api.get("/admin/historical-orders/legacy-status"),
      ]);
      setRows(l.data.orders || []);
      setYears(l.data.years || []);
      setSummary(s.data);
      setLegacy(ls.data);
    } catch {
      toast.error("Couldn't load completed historical orders.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const cleanLegacy = async () => {
    if (!window.confirm(`Back up all ${legacy?.legacy_count} old logged historical orders (kept forever in a backup vault) and clear them so HQ stops showing duplicates?\n\nNothing is lost , everything is preserved in the backup.`)) return;
    setBusy("legacy");
    try {
      const r = await api.post("/admin/historical-orders/backup-and-clear-legacy");
      toast.success(`Backed up ${r.data.backed_up} record(s), cleared ${r.data.cleared}. Duplicates gone.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed.");
    } finally { setBusy(""); }
  };

  const remove = async (o) => {
    if (!window.confirm(`Delete this historical record for ${o.customer_name || o.email}? This can't be undone.`)) return;
    setBusy(o.id);
    try {
      await api.delete(`/admin/historical-orders/${o.id}`);
      toast.success("Record deleted.");
      await load();
    } catch { toast.error("Couldn't delete."); } finally { setBusy(""); }
  };

  return (
    <div data-testid="admin-completed-historical" className="space-y-6" style={{ color: AUBERGINE }}>
      <header>
        <div className="text-[10px] tracking-[0.45em] uppercase font-semibold" style={{ color: BERRY }}>✦ Legacy ledger</div>
        <h1 className="text-4xl mt-1 leading-tight">Completed Historical Orders</h1>
        <p className="italic mt-1 text-sm opacity-70">
          Read-only records of legacy orders. Kept completely separate from live sales, revenue and analytics.
        </p>
      </header>

      {legacy && legacy.legacy_count > 0 && (
        <div className="border-2 rounded-md p-4 flex flex-wrap items-center justify-between gap-3 bg-white" style={{ borderColor: "#C49247" }} data-testid="legacy-cleanup-banner">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5" style={{ color: "#C49247" }} />
            <div className="text-sm">
              <strong>{legacy.legacy_count}</strong> old logged historical order(s) from the retired module still exist (these caused the HQ duplicates).
              {legacy.backup_count > 0 && <span className="opacity-60"> Backup vault already holds {legacy.backup_count}.</span>}
            </div>
          </div>
          <button type="button" onClick={cleanLegacy} disabled={busy === "legacy"} data-testid="legacy-cleanup-btn"
            className="inline-flex items-center gap-2 px-4 h-10 rounded text-white text-xs tracking-[0.15em] uppercase font-semibold disabled:opacity-50"
            style={{ backgroundColor: BERRY }}>
            {busy === "legacy" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            Back up &amp; clear duplicates
          </button>
        </div>
      )}
      {legacy && legacy.legacy_count === 0 && legacy.backup_count > 0 && (
        <div className="text-xs opacity-60 inline-flex items-center gap-1.5" data-testid="legacy-clean-note">
          <ShieldCheck className="w-3.5 h-3.5" style={{ color: GREEN }} /> Legacy duplicates cleared , {legacy.backup_count} record(s) preserved in the backup vault.
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Historical Revenue" value={money(summary?.historical_revenue)} testid="hist-tile-revenue" />
        <Tile label="Historical Orders" value={summary?.historical_order_count ?? 0} testid="hist-tile-orders" />
        <Tile label="Avg Order Value" value={money(summary?.historical_avg_order_value)} testid="hist-tile-aov" />
        <Tile label="Historical Customers" value={summary?.historical_customer_count ?? 0} testid="hist-tile-customers" />
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(e.target.value, year)}
            placeholder="Search name, email, items…" className="w-full h-10 pl-9 pr-3 border rounded" style={{ borderColor: EDGE, backgroundColor: "#fff" }} data-testid="hist-search" />
        </div>
        <select value={year} onChange={(e) => { setYear(e.target.value); load(q, e.target.value); }} className="h-10 px-3 border rounded bg-white" style={{ borderColor: EDGE }} data-testid="hist-year-filter">
          <option value="">All years</option>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <button type="button" onClick={() => load()} className="h-10 px-4 rounded border text-xs tracking-[0.15em] uppercase font-semibold" style={{ borderColor: AUBERGINE }} data-testid="hist-search-btn">Search</button>
      </div>

      <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: EDGE }}>
        {loading ? (
          <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center italic opacity-60" data-testid="hist-empty">
            <Archive className="w-8 h-8 mx-auto mb-3 opacity-40" />
            No completed historical orders yet. Create them from a Historical Customer via Create Order → "Send to Completed Historical Orders".
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] tracking-[0.25em] uppercase" style={{ backgroundColor: CREAM }}>
                <th className="px-4 py-2.5">Completed</th>
                <th className="px-4 py-2.5">Customer</th>
                <th className="px-4 py-2.5 hidden md:table-cell">Items</th>
                <th className="px-4 py-2.5 text-right">Total</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => (
                <React.Fragment key={o.id}>
                  <tr className="border-t cursor-pointer hover:bg-[#FBF6EA]/60" style={{ borderColor: EDGE }} onClick={() => setOpenId(openId === o.id ? null : o.id)} data-testid={`hist-row-${o.id}`}>
                    <td className="px-4 py-2.5 whitespace-nowrap">{String(o.completed_on || "").slice(0, 10)}</td>
                    <td className="px-4 py-2.5">
                      <div className="font-semibold">{o.customer_name || "-"}</div>
                      <div className="text-xs opacity-60">{o.email}</div>
                    </td>
                    <td className="px-4 py-2.5 hidden md:table-cell max-w-sm truncate">{(o.items || "").split("\n")[0]}</td>
                    <td className="px-4 py-2.5 text-right font-semibold">{money(o.total)}</td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      {openId === o.id ? <ChevronUp className="w-4 h-4 inline opacity-50" /> : <ChevronDown className="w-4 h-4 inline opacity-50" />}
                      <button type="button" onClick={(e) => { e.stopPropagation(); remove(o); }} title="Delete record" data-testid={`hist-delete-${o.id}`} className="p-1.5 ml-1 rounded hover:bg-red-50 text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                  {openId === o.id && (
                    <tr className="border-t" style={{ borderColor: EDGE, backgroundColor: CREAM }} data-testid={`hist-detail-${o.id}`}>
                      <td colSpan={5} className="px-6 py-4">
                        <div className="grid sm:grid-cols-2 gap-4 text-sm">
                          <div>
                            <div className="text-[10px] tracking-[0.25em] uppercase font-semibold opacity-60 mb-1">Items</div>
                            <pre className="whitespace-pre-wrap font-sans">{o.items || "-"}</pre>
                            {o.shipping_price > 0 && <div className="mt-1 text-xs opacity-70">Shipping: {money(o.shipping_price)}</div>}
                          </div>
                          <div className="space-y-2">
                            {o.phone && <div><span className="opacity-60">Phone:</span> {o.phone}</div>}
                            {o.address && <div><span className="opacity-60">Address:</span> {o.address}</div>}
                            {o.notes && <div><span className="opacity-60">Notes:</span> {o.notes}</div>}
                            <div><span className="opacity-60">Type:</span> {o.order_type || "-"}</div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Tile({ label, value, testid }) {
  return (
    <div className="bg-white border rounded-md p-4" style={{ borderColor: EDGE }} data-testid={testid}>
      <div className="text-[10px] tracking-[0.3em] uppercase font-semibold opacity-70">{label}</div>
      <div className="text-2xl mt-1 font-semibold" style={{ color: BERRY }}>{value}</div>
    </div>
  );
}
